import { crypto } from "../crypto.js";
import { Dimension } from "dimtext";
import {
  sortableList,
  sortArrayBy,
} from "@local/extensions/collections/sortable-list.js";
import { hydrateType } from "./type.js";
import { hydrateItem } from "./item.js";

import type { DimTextOptions } from "dimtext";
import type { PartialDeep } from "type-fest";
import type { OrderedMap } from "../ordered-map.js";
import type { CustomColumns } from "./column.js";
import type { Fabrication } from "./fabrication.js";
import type { TablesGenerated } from "../main.js";

type Type = TablesGenerated<"types">;
type Item = TablesGenerated<"items">;
type Document = TablesGenerated<"documents">;
type Location = TablesGenerated<"locations">;
type DocumentNote = TablesGenerated<"document_notes">;
type DocumentDimension = TablesGenerated<"document_dimensions">;
type ItemReport = TablesGenerated<"item_reports">;

interface GroupRecordCore {
  data: {
    custom_collection_columns?: object;
    column_order?: object;
    item_order?: object;
    collection_column_order?: object;
    custom_columns?: CustomColumns;
    type_order: Array<string>;
    fabrications: OrderedMap<string, Fabrication>;
    settings: object;
    documents: object;
  };
}

interface GroupRecordComputed {
  dimtext_options?: PartialDeep<DimTextOptions>;
}

interface GroupData extends GroupRecordCore {
  types: Type[];
  items: Item[];
  documents: Document[];
  locations: Location[];
  document_notes: DocumentNote[];
  document_dimensions: DocumentDimension[];
  item_reports: ItemReport[];
}

interface HydratedGroup extends GroupRecordCore {
  types: OrderedMap<string, typeof hydrateType>;
  items: OrderedMap<string, typeof hydrateItem>;
  documents: OrderedMap<string, Document>;
  locations: OrderedMap<string, Location>;
  document_notes: OrderedMap<string, DocumentNote>;
  document_dimensions: OrderedMap<string, DocumentDimension>;
  item_reports: OrderedMap<string, ItemReport>;
}

function createGroup(
  organization_id: string,
  job_id: string,
  {
    data: {
      settings = {},
      fabrications = {
        indices: {},
        order: [],
      },
      ...groupdata
    } = {},
    ...props
  } = {},
) {
  return {
    id: crypto.randomUUID(),
    organization_id,
    job_id,
    seller_reference: null,
    buyer_reference: null,
    project_type: "unconstrained",
    data: {
      settings: {
        dimension_format: "FRACTIONAL",
        decimal_precision: 2,
        fractional_precision: 4,
        mm_precision: 0,
        dim_decimal_precision: 2,
        dim_fractional_precision: 4,
        dim_mm_precision: 0,
        print_location_size: 10,
        display_unit: "inches",
        label_by: "mark",
        collections_label_by: "mark",
        document_display_mode: "view",
        document_sort_by: "created_at",
        hide_width_offset: true,
        hide_height_offset: true,
        summary_default_type_view: -1,
        summary_show_untyped_items: true,
        output_category_callout: "area",
        output_group_by: "type",
        output_show_total_units: true,
        output_show_total_categories: false,
        output_show_total_area: true,
        output_show_total_edge_treatment: true,
        output_show_total_perimeter: true,
        output_show_categories_with_no_items: true,
        output_report_name: "Report",
        output_item_filter: null,
        output_hidden_columns: { "scope": true },
        output_collection_filter: null,
        output_sort_by: null,
        output_sort_collections_by: null,
        output_link_destination: null,
        underlay_opacity: 0.3,
        ...settings,
      },
      fabrications,
      ...groupdata,
    },
    ...props,
  };
}

function hydrateGroupData(data: GroupRecordCore["data"]) {
  data.fabrications?.order.forEach((id) => {
    const fab = data.fabrications[id];
    if (fab.params) {
      fab.params = fab.params.map((p) => {
        return new Dimension(p);
      });
    }
  });
}

function hydrateGroup(group: GroupData): HydratedGroup {
  hydrateGroupData(group.data);

  const g = { ...group };

  g.types = sortableList(
    sortArrayBy(
      group.types.map((t) => hydrateType(t)),
      group.data.type_order,
    ),
  );

  g.items = sortableList(
    sortArrayBy(
      group.items.map((i) => hydrateItem(i, g)),
      group.data.item_order,
    ),
  );

  g.documents = sortableList(group.documents);
  g.locations = sortableList(group.locations);
  g.document_notes = sortableList(group.document_notes);
  g.document_dimensions = sortableList(group.document_dimensions);
  g.item_reports = sortableList(group.item_reports);

  return g;
}

export type { GroupRecordCore, GroupRecordComputed, HydratedGroup };

export { createGroup, hydrateGroup, hydrateGroupData };
